/**
 * Appending search parameters to URL fragments avoiding manual string manipulation
 * [string, string, string |number] => string
 */

export const addSearchParam = (urlFragment: string, name: string, val: string | number) => {
	const fragment = urlFragment.slice(0, 1) === '/' ? urlFragment : '/' + urlFragment
	const urlObj = new URL(`https://foo.bar${fragment}`)

	urlObj.searchParams.append(name, String(val))

	return urlObj.pathname + urlObj.search
}

/**
 * cloneObj
 * Shallow clones Object
 * {} => {}
 */
export const cloneObj = (obj: {}) => JSON.parse(JSON.stringify(obj))

/**
 * cookiesEnabled
 */
export const hasLocalStorage = () => {
	try {
		window.localStorage.setItem('a', 'a')
		const stored = window.localStorage.getItem('a')
		return !!stored
	} catch (e) {
		return false
	}
}

// Check if cookies are available
export const hasCookies = () => {
	try {
		document.cookie = 'test=1'
		const hasCookie = document.cookie.indexOf('test=') !== -1
		document.cookie = 'test=; expires=Thu, 01 Jan 1970 00:00:00 GMT'
		return hasCookie
	} catch (e) {
		return false
	}
}

// Set cookie with expiration
export const setCookie = (name: string, value: string, hours = 1) => {
	if (!hasCookies()) return false

	try {
		const expires = new Date()
		expires.setTime(expires.getTime() + hours * 60 * 60 * 1000)
		document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/`
		return true
	} catch (error) {
		console.warn('Failed to set cookie:', error)
		return false
	}
}

// Get cookie value
export const getCookie = (name: string): string | null => {
	if (!hasCookies()) return null

	try {
		const nameEQ = name + '='
		const ca = document.cookie.split(';')
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i]
			if (!c) continue
			while (c.charAt(0) === ' ') c = c.substring(1, c.length)
			if (c.indexOf(nameEQ) === 0) {
				return decodeURIComponent(c.substring(nameEQ.length, c.length))
			}
		}
		return null
	} catch (error) {
		console.warn('Failed to get cookie:', error)
		return null
	}
}

// Delete cookie
export const deleteCookie = (name: string) => {
	if (!hasCookies()) return false

	try {
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
		return true
	} catch (error) {
		console.warn('Failed to delete cookie:', error)
		return false
	}
}

export const arraySum = (arr: number[]) =>
	arr.reduce((acc: number, cur: number) => {
		acc += cur
		return acc
	}, 0)

// Unified storage helper with automatic fallback
export const storage = {
	/**
	 * Clear multiple keys from storage
	 */
	clear(keys: string[]): boolean {
		let allSuccess = true

		keys.forEach((key) => {
			if (!this.delete(key)) {
				allSuccess = false
			}
		})

		return allSuccess
	},

	/**
	 * Delete data from storage with automatic fallback
	 */
	delete(key: string): boolean {
		let success = false

		// Clear from localStorage
		if (hasLocalStorage()) {
			try {
				localStorage.removeItem(key)
				success = true
			} catch (error) {
				console.warn(`Failed to delete from localStorage: ${key}`, error)
			}
		}

		// Clear from cookies
		if (hasCookies()) {
			if (deleteCookie(key)) {
				success = true
			}
		}

		return success
	},
	/**
	 * Get data from storage with automatic fallback
	 */
	get<T = any>(key: string): T | null {
		// Try localStorage first
		if (hasLocalStorage()) {
			try {
				const cached = localStorage.getItem(key)
				if (cached) {
					// Handle old cache format (with expiresAt and timestamp)
					const parsed = JSON.parse(cached)
					if (parsed && typeof parsed === 'object' && 'data' in parsed && 'expiresAt' in parsed) {
						// Check if expired
						if (Date.now() > parsed.expiresAt) {
							localStorage.removeItem(key)
							return null
						}
						// Migrate to new format
						this.set(key, parsed.data)
						return parsed.data as T
					}

					// Handle new timestamp format
					if (parsed && typeof parsed === 'object' && 'data' in parsed && 'timestamp' in parsed) {
						return parsed.data as T
					}

					// Legacy format (direct data) - migrate to new format
					if (parsed && typeof parsed === 'object') {
						this.set(key, parsed)
						return parsed as T
					}

					// String data
					return parsed as T
				}
			} catch (error) {
				// If JSON parsing failed, it might be a simple string
				const cachedString = localStorage.getItem(key)
				if (cachedString && typeof cachedString === 'string') {
					return cachedString as unknown as T
				}

				console.warn(`Failed to parse localStorage data for ${key}:`, error)
				// Clean up corrupted data
				try {
					localStorage.removeItem(key)
				} catch (e) {
					// Ignore removal errors
				}
			}
		}

		// Fallback to cookies
		if (hasCookies()) {
			try {
				const cached = getCookie(key)
				if (cached) {
					// Handle old cache format (with expiresAt and timestamp)
					const parsed = JSON.parse(cached)
					if (parsed && typeof parsed === 'object' && 'data' in parsed && 'expiresAt' in parsed) {
						// Check if expired
						if (Date.now() > parsed.expiresAt) {
							deleteCookie(key)
							return null
						}
						// Migrate to new format
						this.set(key, parsed.data)
						return parsed.data as T
					}

					// Handle new timestamp format
					if (parsed && typeof parsed === 'object' && 'data' in parsed && 'timestamp' in parsed) {
						return parsed.data as T
					}

					// Legacy format (direct data) - migrate to new format
					if (parsed && typeof parsed === 'object') {
						this.set(key, parsed)
						return parsed as T
					}

					// String data
					return parsed as T
				}
			} catch (error) {
				// If JSON parsing failed, it might be a simple string
				const cachedString = getCookie(key)
				if (cachedString && typeof cachedString === 'string') {
					return cachedString as unknown as T
				}

				console.warn(`Failed to parse cookie data for ${key}:`, error)
				deleteCookie(key)
			}
		}

		return null
	},

	/**
	 * Check if cached item is older than specified hours
	 */
	isOlderThan(key: string, hours: number): boolean {
		// Try localStorage first
		if (hasLocalStorage()) {
			try {
				const cached = localStorage.getItem(key)
				if (cached) {
					const parsed = JSON.parse(cached)
					if (parsed && typeof parsed === 'object' && 'timestamp' in parsed) {
						const ageInHours = (Date.now() - parsed.timestamp) / (1000 * 60 * 60)
						return ageInHours > hours
					}
				}
			} catch (error) {
				console.warn(`Failed to check age for ${key}:`, error)
			}
		}

		// Fallback to cookies
		if (hasCookies()) {
			try {
				const cached = getCookie(key)
				if (cached) {
					const parsed = JSON.parse(cached)
					if (parsed && typeof parsed === 'object' && 'timestamp' in parsed) {
						const ageInHours = (Date.now() - parsed.timestamp) / (1000 * 60 * 60)
						return ageInHours > hours
					}
				}
			} catch (error) {
				console.warn(`Failed to check age for ${key}:`, error)
			}
		}

		return true // Consider missing data as "old"
	},

	/**
	 * Set data in storage with automatic fallback
	 */
	set(key: string, data: any, useExpiration = false): boolean {
		// Add random offset of ±10 minutes to spread out cache expiration times
		// This prevents all smart TVs from hitting the API at exactly the same time
		const randomOffsetMs = (Math.random() - 0.5) * 20 * 60 * 1000 // ±10 minutes in milliseconds

		// Store data with timestamp for age checking
		const dataWithTimestamp = {
			data: data,
			timestamp: Date.now() + randomOffsetMs,
		}

		// For string values, store them directly without JSON.stringify
		const dataString = typeof data === 'string' ? data : JSON.stringify(dataWithTimestamp)

		// Try localStorage first
		if (hasLocalStorage()) {
			try {
				localStorage.setItem(key, dataString)
				return true
			} catch (error) {
				console.warn('localStorage failed, trying cookies:', error)
			}
		}

		// Fallback to cookies
		if (hasCookies()) {
			// Check if data is too large for cookies (4KB limit per cookie)
			if (dataString.length > 4000) {
				console.warn(`Data too large for cookies (${dataString.length} bytes), skipping cache`)
				return false
			}

			const hours = useExpiration ? 1 : 8760 // 1 hour if expiration, 1 year if not
			return setCookie(key, dataString, hours)
		}

		return false
	},
}
